/**
 * WebGL Demos Configuration
 * Centralized configuration for all WebGL demonstrations
 */

// Note: Do not import demo React components here to keep this file server-safe
// import { lazy } from 'react'; // Not currently used
import type { WebGLDemoMetadata } from '@/types/webgl';

// Intentionally empty: React components are resolved client-side in DemoRenderer

// 🌟 World-Class Additions
// const AdvancedParticlePhysicsDemo = lazy(() => import('@/components/webgl/demos/AdvancedParticlePhysics'));
// const AudioReactiveVisualizerDemo = lazy(() => import('@/components/webgl/demos/AudioReactiveVisualizer'));

/**
 * WebGL Demo Categories
 */
export const DEMO_CATEGORIES = {
  PARTICLES: 'Particles',
  NOISE: 'Noise',
  GEOMETRY: 'Geometry',
  PHYSICS: 'Physics',
  TYPOGRAPHY: 'Typography',
  ALGORITHM: 'Algorithm',
  STYLE: 'Style',
  SIMULATION: 'Simulation',
  TIME: 'Time',
  MOTION: 'Motion',
  EFFECT: 'Effect',
  VISUAL_EFFECTS: 'Visual Effects',
  GENERATIVE: 'Generative'
} as const;

/**
 * WebGL Demos Configuration Array
 * Ordered by implementation priority (15 -> 1)
 */
export const WEBGL_DEMOS: WebGLDemoMetadata[] = [
  {
    id: 'hello-demo',
    name: 'Hello Demo',
    slug: '001-hello-demo',
    description: '最小構成のデモ（RotatingCubeを流用）',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 1,
    technologies: ['Three.js', 'WebGL'],
    tags: ['sample'],
    icon: '👋',
    features: ['最小構成']
  },
  {
    id: 'rotating-cube',
    name: 'Rotating Cube',
    slug: '002-rotating-cube',
    description: 'シンプルな回転するキューブのWebGLデモ。マウスでライトの位置を操作できます。',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 1,
    technologies: ['Three.js', 'WebGL', 'Basic Geometry'],
    tags: ['3D', 'interactive', 'beginner'],
    icon: '🎲',
    features: ['回転アニメーション', 'マウスインタラクション', '色変化エフェクト'],
    performance: {
      targetFps: 60,
      maxMemoryMB: 64,
      gpuIntensive: false
    }
  },
  {
    id: 'fluid-simulation',
    name: 'Fluid Simulation',
    slug: '003-fluid-simulation',
    description: 'Interactive fluid dynamics simulation with colorful dye advection',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['WebGL', 'GLSL', 'Fluid Dynamics'],
    tags: ['simulation', 'interactive', 'physics', 'shaders'],
    icon: '💧',
    features: ['Real-time fluid dynamics', 'Interactive mouse/touch', 'Colorful dye advection'],
    performance: {
      targetFps: 60,
      maxMemoryMB: 256,
      gpuIntensive: true
    }
  },
  {
    id: '0_demo000',
    name: 'Demo000',
    slug: '004-demo-000',
    description: 'TSLインスパイアのモーションデザイン表現（完全コピー参考実装）',
    category: DEMO_CATEGORIES.MOTION,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'GLSL'],
    features: ['モーショングラフィックス', '極座標歪み', '色相シフト']
  },
  // 🌟 WORLD-CLASS SHOWCASES (NEW)
  {
    id: '16_advanced_particle_physics',
    name: 'Advanced Particle Physics',
    slug: '005-advanced-particle-physics',
    description: 'GPU Compute Shaders を活用した高度なパーティクル物理シミュレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'WebGL', 'Compute Shaders', 'GPGPU'],
    features: ['重力/反発', '衝突', '擬似流体', 'マウスインタラクション'],
    performance: {
      targetFps: 60,
      maxMemoryMB: 512,
      gpuIntensive: true
    }
  },
  // 追加候補（将来実装）: Audio Reactive Visualizer
  {
    id: '15_one_million_particles',
    name: 'One Million Particles',
    slug: '006-one-million-particles',
    description: '100万個のパーティクルによる大規模パーティクルシステム',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'WebGL', 'Instancing', 'Compute Shaders'],
    features: ['マウスインタラクション', '重力シミュレーション', 'パフォーマンス最適化'],
    performance: {
      targetFps: 60,
      maxMemoryMB: 256,
      gpuIntensive: true
    }
  },
  {
    id: '13_triangular_sphere',
    name: 'Triangular Sphere',
    slug: '007-triangular-sphere',
    description: '三角形で構成された球体の動的変形',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Geometry Manipulation', 'Vertex Shaders'],
    features: ['動的メッシュ変形', 'ノイズベース変形', 'リアルタイム計算']
  },
  
  {
    id: '12_triangles2',
    name: 'Triangles 2',
    slug: '008-triangles-2',
    description: '三角形パターンの応用版（複雑な変形アルゴリズム）',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Advanced Geometry', 'Complex Algorithms'],
    features: ['複雑な変形', '高度なアルゴリズム', 'パフォーマンス最適化']
  },
  {
    id: '11_triangles1',
    name: 'Triangles 1',
    slug: '009-triangles-1',
    description: '三角形パターンの基本版（幾何学的アニメーション）',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Basic Geometry', 'Animation'],
    features: ['基本的な変形', '幾何学パターン', 'スムーズアニメーション']
  },
  {
    id: '10_delaunay',
    name: 'Delaunay',
    slug: '010-delaunay',
    description: 'ドロネー三角分割による動的ポイント配置',
    category: DEMO_CATEGORIES.ALGORITHM,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Computational Geometry', 'Delaunay Triangulation'],
    features: ['動的三角分割', 'ポイント配置', 'リアルタイム計算']
  },
  {
    id: '9_illustrized',
    name: 'Illustrized',
    slug: '011-illustrized',
    description: 'イラストレーション風表現とアーティスティックエフェクト',
    category: DEMO_CATEGORIES.STYLE,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Post-processing', 'Artistic Effects'],
    features: ['イラスト風レンダリング', 'アーティスティックフィルター', 'スタイル変換']
  },
  {
    id: '8_simplex_noise_mono',
    name: 'Simplex Noise Mono',
    slug: '012-simplex-noise-mono',
    description: 'モノクロ版Simplex Noise（パフォーマンス最適化版）',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Simplex Noise', 'Performance Optimization'],
    features: ['モノクロ表現', 'パフォーマンス最適化', 'リアルタイム生成']
  },
  {
    id: '7_ripple_sphere',
    name: 'Ripple Sphere',
    slug: '013-ripple-sphere',
    description: '球面上の波紋効果とインタラクティブ波形生成',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Wave Simulation', 'Sphere Geometry'],
    features: ['球面波紋', 'インタラクティブ波形', '物理シミュレーション']
  },
  {
    id: '6_spiritual_boids',
    name: 'Spiritual Boids',
    slug: '014-spiritual-boids',
    description: 'Boidアルゴリズムによる群衆行動シミュレーション',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Boid Algorithm', '3D Simulation'],
    features: ['群衆行動', '3D空間での動き', 'AI的行動パターン']
  },
  {
    id: '5_simplex_noise',
    name: 'Simplex Noise',
    slug: '015-simplex-noise',
    description: 'カラフルなSimplex Noiseパターンとリアルタイム生成',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Simplex Noise', 'Color Mapping'],
    features: ['カラフルなノイズ', 'リアルタイム生成', 'パラメータ調整']
  },
  {
    id: '4_noise_clock',
    name: 'Noise Clock',
    slug: '016-noise-clock',
    description: 'ノイズベース時計表示とリアルタイム時刻同期',
    category: DEMO_CATEGORIES.TIME,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Noise Generation', 'Time API'],
    features: ['ノイズベース表示', 'リアルタイム時刻', '視覚的時計']
  },
  {
    id: '3_spinning_noise',
    name: 'Spinning Noise',
    slug: '017-spinning-noise',
    description: '回転するノイズパターンと時間ベースアニメーション',
    category: DEMO_CATEGORIES.MOTION,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Rotation Animation', 'Noise Patterns'],
    features: ['回転アニメーション', '時間ベース変化', 'ノイズパターン']
  },
  {
    id: '2_mirage_noise',
    name: 'Mirage Noise',
    slug: '018-mirage-noise',
    description: '蜃気楼効果のノイズと光学的錯覚表現',
    category: DEMO_CATEGORIES.EFFECT,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Optical Effects', 'Heat Shimmer'],
    features: ['蜃気楼効果', '光学的錯覚', '熱気表現']
  },
  {
    id: '1_surface_wave',
    name: 'Surface Wave',
    slug: '019-surface-wave',
    description: '水面波シミュレーションとWebカメラ連携機能',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Wave Simulation', 'WebRTC'],
    features: ['水面波シミュレーション', 'Webカメラ連携', '物理演算'],
    performance: {
      targetFps: 60,
      maxMemoryMB: 128,
      gpuIntensive: false
    }
  },
  // 17-100: 追加デモ群
  {
    id: '17_fractal_tree',
    name: 'Fractal Tree',
    slug: '020-fractal-tree',
    description: 'フラクタル構造の樹木生成アルゴリズム',
    category: DEMO_CATEGORIES.ALGORITHM,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Fractals', 'Procedural Generation'],
    features: ['フラクタル生成', '動的成長', '再帰アルゴリズム'],
    icon: '🌲'
  },
  {
    id: '18_dna_helix',
    name: 'DNA Helix',
    slug: '021-dna-helix',
    description: 'DNA二重螺旋構造の3D可視化',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Spiral Geometry', 'Bio Visualization'],
    features: ['3D螺旋構造', '生物学可視化', '動的回転'],
    icon: '🧬'
  },
  {
    id: '19_galaxy_spiral',
    name: 'Galaxy Spiral',
    slug: '022-galaxy-spiral',
    description: '銀河系螺旋腕の形成シミュレーション',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Astrophysics', 'Particle Systems'],
    features: ['天体物理学', '大規模シミュレーション', '螺旋構造'],
    icon: '🌌'
  },
  {
    id: '20_crystal_growth',
    name: 'Crystal Growth',
    slug: '023-crystal-growth',
    description: '結晶成長パターンの可視化',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Crystal Formation', 'Growth Algorithms'],
    features: ['結晶成長', '鉱物学シミュレーション', '幾何学的成長'],
    icon: '💎'
  },
  {
    id: '21_mandelbrot_zoom',
    name: 'Mandelbrot Zoom',
    slug: '024-mandelbrot-zoom',
    description: 'マンデルブロ集合の無限ズーム',
    category: DEMO_CATEGORIES.ALGORITHM,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Complex Numbers', 'Infinite Zoom'],
    features: ['フラクタル数学', '無限ズーム', '複素数計算'],
    icon: '🔢'
  },
  {
    id: '22_lissajous_curves',
    name: 'Lissajous Curves',
    slug: '025-lissajous-curves',
    description: 'リサージュ図形の3D表現',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Parametric Curves', 'Harmonic Motion'],
    features: ['パラメトリック曲線', '調和運動', '数学的美'],
    icon: '〰️'
  },
  {
    id: '23_lorenz_attractor',
    name: 'Lorenz Attractor',
    slug: '026-lorenz-attractor',
    description: 'ローレンツアトラクターの3D軌道',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Chaos Theory', 'Differential Equations'],
    features: ['カオス理論', '軌道計算', '非線形システム'],
    icon: '🌀'
  },
  {
    id: '24_voronoi_diagram',
    name: 'Voronoi Diagram',
    slug: '027-voronoi-diagram',
    description: 'ボロノイ図の動的生成と可視化',
    category: DEMO_CATEGORIES.ALGORITHM,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Voronoi Algorithm', 'Computational Geometry'],
    features: ['ボロノイ分割', '動的配置', '幾何学的分析'],
    icon: '🔷'
  },
  {
    id: '25_solar_system',
    name: 'Solar System',
    slug: '028-solar-system',
    description: '太陽系惑星軌道の正確なシミュレーション',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Orbital Mechanics', 'Astronomy'],
    features: ['軌道力学', '天体運動', '正確な物理計算'],
    icon: '☀️'
  },
  {
    id: '26_fiber_optics',
    name: 'Fiber Optics',
    slug: '029-fiber-optics',
    description: '光ファイバー内の光伝播シミュレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Optics', 'Light Physics'],
    features: ['光学シミュレーション', '光の伝播', '物理法則'],
    icon: '🔦'
  },
  {
    id: '27_neural_network',
    name: 'Neural Network',
    slug: '030-neural-network',
    description: 'ニューラルネットワークの3D可視化',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'AI Visualization', 'Neural Networks'],
    features: ['AI可視化', 'ネットワーク構造', '学習過程'],
    icon: '🧠'
  },
  {
    id: '28_plasma_field',
    name: 'Plasma Field',
    slug: '031-plasma-field',
    description: 'プラズマ場の電磁気シミュレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Electromagnetic Fields', 'Plasma Physics'],
    features: ['電磁場計算', 'プラズマ物理学', '高エネルギー物理'],
    icon: '⚡'
  },
  {
    id: '29_quantum_interference',
    name: 'Quantum Interference',
    slug: '032-quantum-interference',
    description: '量子干渉パターンの可視化',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Quantum Mechanics', 'Wave Interference'],
    features: ['量子力学', '干渉パターン', '波動関数'],
    icon: '🌊'
  },
  {
    id: '30_cellular_automata',
    name: 'Cellular Automata',
    slug: '033-cellular-automata',
    description: 'セルラーオートマトンの進化パターン',
    category: DEMO_CATEGORIES.ALGORITHM,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Cellular Automata', 'Emergence'],
    features: ['セルラーオートマトン', '自己組織化', '創発現象'],
    icon: '🔬'
  },
  {
    id: '31_magnetic_field',
    name: 'Magnetic Field',
    slug: '034-magnetic-field',
    description: '磁場線の3D可視化',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Magnetic Fields', 'Vector Fields'],
    features: ['磁場可視化', 'ベクトル場', '電磁気学'],
    icon: '🧲'
  },
  {
    id: '32_sound_waves',
    name: 'Sound Waves',
    slug: '035-sound-waves',
    description: '音波の3D伝播シミュレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Sound Physics', 'Wave Propagation'],
    features: ['音響学', '波動伝播', '3D音場'],
    icon: '🔊'
  },
  {
    id: '33_protein_folding',
    name: 'Protein Folding',
    slug: '036-protein-folding',
    description: 'タンパク質フォールディングの分子動力学',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Molecular Dynamics', 'Biochemistry'],
    features: ['分子生物学', 'タンパク質構造', '分子間力'],
    icon: '🧬'
  },
  {
    id: '34_weather_system',
    name: 'Weather System',
    slug: '037-weather-system',
    description: '気象システムの大気シミュレーション',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Atmospheric Physics', 'Fluid Dynamics'],
    features: ['気象学', '大気力学', '流体シミュレーション'],
    icon: '🌤️'
  },
  {
    id: '35_ecosystem_dynamics',
    name: 'Ecosystem Dynamics',
    slug: '038-ecosystem-dynamics',
    description: '生態系の捕食者-被食者モデル',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Population Dynamics', 'Ecology'],
    features: ['生態学', '個体群動態', '生物間相互作用'],
    icon: '🦋'
  },
  {
    id: '36_fire_simulation',
    name: 'Fire Simulation',
    slug: '039-fire-simulation',
    description: '炎の物理シミュレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Combustion Physics', 'Particle Systems'],
    features: ['燃焼物理学', '炎の表現', '熱力学'],
    icon: '🔥'
  },
  {
    id: '37_ocean_currents',
    name: 'Ocean Currents',
    slug: '040-ocean-currents',
    description: '海流の地球規模シミュレーション',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Oceanography', 'Global Circulation'],
    features: ['海洋学', '地球規模循環', '海流パターン'],
    icon: '🌊'
  },
  {
    id: '38_seismic_waves',
    name: 'Seismic Waves',
    slug: '041-seismic-waves',
    description: '地震波の地球内部伝播',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Seismology', 'Wave Propagation'],
    features: ['地震学', '地球内部構造', '波動伝播'],
    icon: '🌍'
  },
  {
    id: '39_aurora_borealis',
    name: 'Aurora Borealis',
    slug: '042-aurora-borealis',
    description: 'オーロラの電磁気現象シミュレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Electromagnetic Physics', 'Atmospheric Science'],
    features: ['電磁気学', '大気物理学', '宇宙天気'],
    icon: '🌌'
  },
  {
    id: '40_black_hole',
    name: 'Black Hole',
    slug: '043-black-hole',
    description: 'ブラックホールの重力レンズ効果',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'General Relativity', 'Gravitational Lensing'],
    features: ['相対性理論', '重力レンズ', '時空歪み'],
    icon: '🕳️'
  },
  {
    id: '41_quantum_tunneling',
    name: 'Quantum Tunneling',
    slug: '044-quantum-tunneling',
    description: '量子トンネル効果の可視化',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Quantum Mechanics', 'Wave Functions'],
    features: ['量子力学', 'トンネル効果', '波動関数'],
    icon: '🔬'
  },
  {
    id: '42_supernova_explosion',
    name: 'Supernova Explosion',
    slug: '045-supernova-explosion',
    description: '超新星爆発の星の進化シミュレーション',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Stellar Evolution', 'Nuclear Physics'],
    features: ['恒星進化', '核物理学', '超新星爆発'],
    icon: '💥'
  },
  {
    id: '43_crystal_optics',
    name: 'Crystal Optics',
    slug: '046-crystal-optics',
    description: '結晶内の光学特性シミュレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Crystal Optics', 'Light Refraction'],
    features: ['結晶光学', '屈折現象', '偏光'],
    icon: '💎'
  },
  {
    id: '44_plasma_waves',
    name: 'Plasma Waves',
    slug: '047-plasma-waves',
    description: 'プラズマ中の波動現象',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Plasma Physics', 'Wave Dynamics'],
    features: ['プラズマ物理学', '波動現象', '電離気体'],
    icon: '⚡'
  },
  {
    id: '45_photosynthesis',
    name: 'Photosynthesis',
    slug: '048-photosynthesis',
    description: '光合成の分子レベルシミュレーション',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Biochemistry', 'Molecular Biology'],
    features: ['光合成', '分子生物学', 'エネルギー変換'],
    icon: '🌱'
  },
  {
    id: '46_semiconductor_physics',
    name: 'Semiconductor Physics',
    slug: '049-semiconductor-physics',
    description: '半導体内の電子移動シミュレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Solid State Physics', 'Electronics'],
    features: ['半導体物理学', '電子移動', '固体物理学'],
    icon: '💻'
  },
  {
    id: '47_turbulence_flow',
    name: 'Turbulence Flow',
    slug: '050-turbulence-flow',
    description: '乱流の複雑な流体現象',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Turbulence', 'Complex Fluid Dynamics'],
    features: ['乱流現象', '複雑流体', 'カオス的流れ'],
    icon: '🌪️'
  },
  {
    id: '48_metamaterial',
    name: 'Metamaterial',
    slug: '051-metamaterial',
    description: 'メタマテリアルの特異な光学特性',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Metamaterials', 'Advanced Optics'],
    features: ['メタマテリアル', '負の屈折率', '先端光学'],
    icon: '🔮'
  },
  {
    id: '49_virus_spread',
    name: 'Virus Spread',
    slug: '052-virus-spread',
    description: 'ウイルス感染拡散の疫学モデル',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Epidemiology', 'Mathematical Modeling'],
    features: ['疫学モデル', '感染拡散', '数理モデル'],
    icon: '🦠'
  },
  {
    id: '50_hologram',
    name: 'Hologram',
    slug: '053-hologram',
    description: 'ホログラムの干渉パターン生成',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Holography', 'Optical Interference'],
    features: ['ホログラフィー', '光学干渉', '3D記録'],
    icon: '👻'
  },
  {
    id: '51_topology_surface',
    name: 'Topology Surface',
    slug: '054-topology-surface',
    description: 'トポロジー的表面の変形',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Topology', 'Surface Mathematics'],
    features: ['位相幾何学', '表面変形', '数学的構造'],
    icon: '🎭'
  },
  {
    id: '52_laser_diffraction',
    name: 'Laser Diffraction',
    slug: '055-laser-diffraction',
    description: 'レーザー回折パターンの物理現象',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Laser Physics', 'Diffraction'],
    features: ['レーザー物理学', '回折現象', '波動光学'],
    icon: '🔴'
  },
  {
    id: '53_superconductor',
    name: 'Superconductor',
    slug: '056-superconductor',
    description: '超伝導体のマイスナー効果',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Superconductivity', 'Magnetic Levitation'],
    features: ['超伝導', 'マイスナー効果', '磁気浮上'],
    icon: '🧊'
  },
  {
    id: '54_cosmic_microwave',
    name: 'Cosmic Microwave',
    slug: '057-cosmic-microwave',
    description: '宇宙マイクロ波背景放射の可視化',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Cosmology', 'Big Bang Theory'],
    features: ['宇宙論', 'ビッグバン', '宇宙背景放射'],
    icon: '🌌'
  },
  {
    id: '55_nanoparticle',
    name: 'Nanoparticle',
    slug: '058-nanoparticle',
    description: 'ナノ粒子の自己組織化',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Nanotechnology', 'Self-Assembly'],
    features: ['ナノテクノロジー', '自己組織化', '分子間力'],
    icon: '⚛️'
  },
  {
    id: '56_butterfly_effect',
    name: 'Butterfly Effect',
    slug: '059-butterfly-effect',
    description: 'バタフライ効果のカオス理論',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Chaos Theory', 'Sensitive Dependence'],
    features: ['カオス理論', '初期値依存', '非線形動力学'],
    icon: '🦋'
  },
  {
    id: '57_photonic_crystal',
    name: 'Photonic Crystal',
    slug: '060-photonic-crystal',
    description: 'フォトニック結晶の光制御',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Photonic Crystals', 'Light Control'],
    features: ['フォトニック結晶', '光制御', 'バンドギャップ'],
    icon: '🔶'
  },
  {
    id: '58_quantum_computer',
    name: 'Quantum Computer',
    slug: '061-quantum-computer',
    description: '量子コンピューターの量子ビット操作',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Quantum Computing', 'Qubits'],
    features: ['量子計算', '量子ビット', '量子ゲート'],
    icon: '💾'
  },
  {
    id: '59_fusion_reactor',
    name: 'Fusion Reactor',
    slug: '062-fusion-reactor',
    description: '核融合炉のプラズマ閉じ込め',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Nuclear Fusion', 'Plasma Confinement'],
    features: ['核融合', 'プラズマ閉じ込め', '核物理学'],
    icon: '☢️'
  },
  {
    id: '60_dark_matter',
    name: 'Dark Matter',
    slug: '063-dark-matter',
    description: 'ダークマターの重力効果シミュレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Dark Matter', 'Gravitational Effects'],
    features: ['ダークマター', '重力効果', '宇宙構造形成'],
    icon: '🌑'
  },
  {
    id: '61_graphene_physics',
    name: 'Graphene Physics',
    slug: '064-graphene-physics',
    description: 'グラフェンの電子特性',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Graphene', 'Electronic Properties'],
    features: ['グラフェン', '電子物性', '2次元材料'],
    icon: '📄'
  },
  {
    id: '62_brain_network',
    name: 'Brain Network',
    slug: '065-brain-network',
    description: '脳神経ネットワークの情報伝達',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Neuroscience', 'Neural Networks'],
    features: ['神経科学', 'ニューロン結合', '情報伝達'],
    icon: '🧠'
  },
  {
    id: '63_climate_model',
    name: 'Climate Model',
    slug: '066-climate-model',
    description: '地球気候システムモデル',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Climate Science', 'Global Modeling'],
    features: ['気候科学', '地球システム', '温暖化モデル'],
    icon: '🌍'
  },
  {
    id: '64_enzyme_kinetics',
    name: 'Enzyme Kinetics',
    slug: '067-enzyme-kinetics',
    description: '酵素反応速度論の分子動力学',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Biochemistry', 'Enzyme Kinetics'],
    features: ['酵素反応', '生化学', '反応速度論'],
    icon: '🧪'
  },
  {
    id: '65_space_debris',
    name: 'Space Debris',
    slug: '068-space-debris',
    description: '宇宙デブリの軌道追跡',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Orbital Mechanics', 'Space Safety'],
    features: ['軌道力学', '宇宙安全', 'デブリ追跡'],
    icon: '🛰️'
  },
  {
    id: '66_ionic_crystal',
    name: 'Ionic Crystal',
    slug: '069-ionic-crystal',
    description: 'イオン結晶の構造と振動',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Crystal Structure', 'Ionic Bonding'],
    features: ['結晶構造', 'イオン結合', '格子振動'],
    icon: '🔷'
  },
  {
    id: '67_antimatter',
    name: 'Antimatter',
    slug: '070-antimatter',
    description: '反物質と物質の対消滅',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Particle Physics', 'Antimatter'],
    features: ['反物質', '素粒子物理学', '対消滅'],
    icon: '💫'
  },
  {
    id: '68_photovoltaic',
    name: 'Photovoltaic',
    slug: '071-photovoltaic',
    description: '太陽電池の光電効果',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Photovoltaic Effect', 'Solar Energy'],
    features: ['光電効果', '太陽エネルギー', '半導体接合'],
    icon: '☀️'
  },
  {
    id: '69_muscle_contraction',
    name: 'Muscle Contraction',
    slug: '072-muscle-contraction',
    description: '筋収縮の分子メカニズム',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Molecular Biology', 'Muscle Physiology'],
    features: ['筋生理学', '分子機構', 'アクチンミオシン'],
    icon: '💪'
  },
  {
    id: '70_laser_cooling',
    name: 'Laser Cooling',
    slug: '073-laser-cooling',
    description: 'レーザー冷却による原子操作',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Laser Cooling', 'Atomic Physics'],
    features: ['レーザー冷却', '原子物理学', 'ドップラー効果'],
    icon: '❄️'
  },
  {
    id: '71_cosmic_ray',
    name: 'Cosmic Ray',
    slug: '074-cosmic-ray',
    description: '宇宙線の大気相互作用',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Cosmic Rays', 'Atmospheric Physics'],
    features: ['宇宙線', '大気相互作用', '高エネルギー物理'],
    icon: '🌠'
  },
  {
    id: '72_membrane_transport',
    name: 'Membrane Transport',
    slug: '075-membrane-transport',
    description: '細胞膜輸送の分子機構',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Cell Biology', 'Membrane Transport'],
    features: ['細胞生物学', '膜輸送', '分子機構'],
    icon: '🧫'
  },
  {
    id: '73_higgs_field',
    name: 'Higgs Field',
    slug: '076-higgs-field',
    description: 'ヒッグス場の質量生成機構',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Particle Physics', 'Higgs Mechanism'],
    features: ['ヒッグス機構', '素粒子物理学', '質量生成'],
    icon: '⚛️'
  },
  {
    id: '74_stellar_nursery',
    name: 'Stellar Nursery',
    slug: '077-stellar-nursery',
    description: '星形成領域の天体物理学',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Star Formation', 'Astrophysics'],
    features: ['星形成', '天体物理学', '原始惑星系円盤'],
    icon: '🌟'
  },
  {
    id: '75_quantum_dots',
    name: 'Quantum Dots',
    slug: '078-quantum-dots',
    description: '量子ドットの電子閉じ込め',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Quantum Dots', 'Nanoelectronics'],
    features: ['量子ドット', 'ナノエレクトロニクス', '電子閉じ込め'],
    icon: '🔴'
  },
  {
    id: '76_bacterial_growth',
    name: 'Bacterial Growth',
    slug: '079-bacterial-growth',
    description: '細菌増殖の指数関数的成長',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Microbiology', 'Exponential Growth'],
    features: ['微生物学', '指数関数的成長', '細菌培養'],
    icon: '🦠'
  },
  {
    id: '77_optical_fiber',
    name: 'Optical Fiber',
    slug: '080-optical-fiber',
    description: '光ファイバーの全反射現象',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Fiber Optics', 'Total Reflection'],
    features: ['光ファイバー', '全反射', '光通信'],
    icon: '🔆'
  },
  {
    id: '78_radioactive_decay',
    name: 'Radioactive Decay',
    slug: '081-radioactive-decay',
    description: '放射性崩壊の確率過程',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Nuclear Physics', 'Radioactive Decay'],
    features: ['放射性崩壊', '核物理学', '確率過程'],
    icon: '☢️'
  },
  {
    id: '79_polymer_dynamics',
    name: 'Polymer Dynamics',
    slug: '082-polymer-dynamics',
    description: '高分子鎖の動力学',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Polymer Science', 'Chain Dynamics'],
    features: ['高分子科学', '鎖動力学', '高分子物性'],
    icon: '🔗'
  },
  {
    id: '80_gravitational_waves',
    name: 'Gravitational Waves',
    slug: '083-gravitational-waves',
    description: '重力波の時空歪み伝播',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'General Relativity', 'Gravitational Waves'],
    features: ['重力波', '一般相対性理論', '時空歪み'],
    icon: '🌊'
  },
  {
    id: '81_crystal_defects',
    name: 'Crystal Defects',
    slug: '084-crystal-defects',
    description: '結晶欠陥の材料科学',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Materials Science', 'Crystal Defects'],
    features: ['材料科学', '結晶欠陥', '転位'],
    icon: '💎'
  },
  {
    id: '82_x_ray_diffraction',
    name: 'X-ray Diffraction',
    slug: '085-x-ray-diffraction',
    description: 'X線回折による構造解析',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'X-ray Crystallography', 'Structure Analysis'],
    features: ['X線結晶学', '構造解析', 'ブラッグ回折'],
    icon: '🔬'
  },
  {
    id: '83_blood_flow',
    name: 'Blood Flow',
    slug: '086-blood-flow',
    description: '血管内の血流動力学',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Hemodynamics', 'Fluid Mechanics'],
    features: ['血流動力学', '流体力学', '循環器生理学'],
    icon: '❤️'
  },
  {
    id: '84_supernova_remnant',
    name: 'Supernova Remnant',
    slug: '087-supernova-remnant',
    description: '超新星残骸の衝撃波進化',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Shock Waves', 'Astrophysics'],
    features: ['衝撃波', '天体物理学', '超新星残骸'],
    icon: '💥'
  },
  {
    id: '85_nuclear_fission',
    name: 'Nuclear Fission',
    slug: '088-nuclear-fission',
    description: '核分裂連鎖反応',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Nuclear Physics', 'Chain Reaction'],
    features: ['核分裂', '連鎖反応', '原子力'],
    icon: '⚛️'
  },
  {
    id: '86_protein_synthesis',
    name: 'Protein Synthesis',
    slug: '089-protein-synthesis',
    description: 'タンパク質合成の分子機構',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Molecular Biology', 'Protein Synthesis'],
    features: ['タンパク質合成', '分子生物学', '遺伝情報発現'],
    icon: '🧬'
  },
  {
    id: '87_ferromagnetism',
    name: 'Ferromagnetism',
    slug: '090-ferromagnetism',
    description: '強磁性体の磁区構造',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Magnetism', 'Magnetic Domains'],
    features: ['強磁性', '磁区構造', '磁性材料'],
    icon: '🧲'
  },
  {
    id: '88_mitosis',
    name: 'Mitosis',
    slug: '091-mitosis',
    description: '細胞分裂の動的過程',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Cell Biology', 'Cell Division'],
    features: ['細胞分裂', '細胞生物学', 'DNA複製'],
    icon: '🔬'
  },
  {
    id: '89_superfluidity',
    name: 'Superfluidity',
    slug: '092-superfluidity',
    description: '超流動の量子現象',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Quantum Physics', 'Superfluidity'],
    features: ['超流動', '量子現象', 'ボース・アインシュタイン凝縮'],
    icon: '🌊'
  },
  {
    id: '90_galaxy_collision',
    name: 'Galaxy Collision',
    slug: '093-galaxy-collision',
    description: '銀河衝突のN体シミュレーション',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'N-body Simulation', 'Galactic Dynamics'],
    features: ['銀河衝突', 'N体シミュレーション', '銀河動力学'],
    icon: '🌌'
  },
  {
    id: '91_electron_beam',
    name: 'Electron Beam',
    slug: '094-electron-beam',
    description: '電子ビームの磁場偏向',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Electron Optics', 'Electromagnetic Fields'],
    features: ['電子光学', '電磁場', 'ビーム偏向'],
    icon: '⚡'
  },
  {
    id: '92_molecular_motor',
    name: 'Molecular Motor',
    slug: '095-molecular-motor',
    description: '分子モーターの機械的運動',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Molecular Biology', 'Mechanical Motion'],
    features: ['分子モーター', '機械的運動', '生体分子機械'],
    icon: '⚙️'
  },
  {
    id: '93_josephson_junction',
    name: 'Josephson Junction',
    slug: '096-josephson-junction',
    description: 'ジョセフソン接合の量子効果',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Superconductivity', 'Josephson Effect'],
    features: ['ジョセフソン効果', '超伝導', '量子接合'],
    icon: '🔌'
  },
  {
    id: '94_embryo_development',
    name: 'Embryo Development',
    slug: '097-embryo-development',
    description: '胚発生の形態形成',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Developmental Biology', 'Morphogenesis'],
    features: ['発生生物学', '形態形成', '胚発生'],
    icon: '🐣'
  },
  {
    id: '95_phonon_dispersion',
    name: 'Phonon Dispersion',
    slug: '098-phonon-dispersion',
    description: 'フォノン分散関係',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Solid State Physics', 'Phonon Physics'],
    features: ['フォノン', '固体物理学', '格子振動'],
    icon: '🔊'
  },
  {
    id: '96_exoplanet_transit',
    name: 'Exoplanet Transit',
    slug: '099-exoplanet-transit',
    description: '系外惑星のトランジット現象',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Exoplanet Detection', 'Transit Photometry'],
    features: ['系外惑星', 'トランジット', '測光観測'],
    icon: '🪐'
  },
  {
    id: '97_spin_glass',
    name: 'Spin Glass',
    slug: '100-spin-glass',
    description: 'スピングラスの磁気フラストレーション',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Statistical Mechanics', 'Frustrated Systems'],
    features: ['スピングラス', '統計力学', 'フラストレーション'],
    icon: '🌀'
  },
  {
    id: '98_stem_cell',
    name: 'Stem Cell',
    slug: '101-stem-cell',
    description: '幹細胞の分化メカニズム',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Stem Cell Biology', 'Cell Differentiation'],
    features: ['幹細胞', '細胞分化', '再生医学'],
    icon: '🧬'
  },
  {
    id: '99_neutron_star',
    name: 'Neutron Star',
    slug: '102-neutron-star',
    description: '中性子星の極限物理状態',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Neutron Star Physics', 'Extreme Matter'],
    features: ['中性子星', '極限物理', 'パルサー'],
    icon: '⚫'
  },
  {
    id: '100_string_theory',
    name: 'String Theory',
    slug: '103-string-theory',
    description: '超弦理論の高次元空間',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'String Theory', 'Higher Dimensions'],
    features: ['超弦理論', '高次元', '統一理論'],
    icon: '🎻'
  }
];;

/**
 * Get implemented demos only
 */
export const getImplementedDemos = (): WebGLDemoMetadata[] => {
  // Note: コンポーネントはクライアント側で遅延解決するため、ここでは implemented のみで判定する
  return WEBGL_DEMOS.filter(demo => demo.implemented);
};

/**
 * Get demo by slug
 */
export const getDemoBySlug = (slug: string): WebGLDemoMetadata | undefined => {
  return WEBGL_DEMOS.find(demo => demo.slug === slug);
};

/**
 * Get demo by ID
 */
export const getDemoById = (id: string): WebGLDemoMetadata | undefined => {
  return WEBGL_DEMOS.find(demo => demo.id === id);
};

/**
 * Get demos by category
 */
export const getDemosByCategory = (category: string): WebGLDemoMetadata[] => {
  return WEBGL_DEMOS.filter(demo => demo.category === category);
};

/**
 * Get random implemented demo
 */
export const getRandomImplementedDemo = (): WebGLDemoMetadata | null => {
  const implementedDemos = getImplementedDemos();
  if (implementedDemos.length === 0) return null;
  
  const randomIndex = Math.floor(Math.random() * implementedDemos.length);
  return implementedDemos[randomIndex];
};

/**
 * Pre-computed lookup maps for O(1) performance
 */
export const DEMO_BY_ID_MAP = WEBGL_DEMOS.reduce((map, demo) => {
  map[demo.id] = demo;
  return map;
}, {} as Record<string, WebGLDemoMetadata>);

export const DEMO_BY_SLUG_MAP = WEBGL_DEMOS.reduce((map, demo) => {
  map[demo.slug] = demo;
  return map;
}, {} as Record<string, WebGLDemoMetadata>);

/**
 * Demo statistics
 */
export const getDemoStatistics = () => {
  const total = WEBGL_DEMOS.length;
  const implemented = getImplementedDemos().length;
  const pending = total - implemented;
  
  const categoryCounts = WEBGL_DEMOS.reduce((acc, demo) => {
    acc[demo.category] = (acc[demo.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const difficultyDistribution = WEBGL_DEMOS.reduce((acc, demo) => {
    const difficulty = demo.difficulty || 1;
    acc[difficulty] = (acc[difficulty] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);
  
  return {
    total,
    implemented,
    pending,
    implementationRate: Math.round((implemented / total) * 100),
    categoryCounts,
    difficultyDistribution
  };
};

// Phase 1 New Demos (Template-based)
WEBGL_DEMOS.push(
  {
    id: 'gravity-simulation',
    name: 'Gravity Simulation',
    slug: 'gravity-simulation',
    description: 'Multiple gravity wells creating complex orbital patterns',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Physics', 'Particles'],
    tags: ['gravity', 'orbital', 'simulation'],
    icon: '🌌',
    features: ['Multiple gravity wells', 'Particle dynamics', 'Real-time physics']
  },
  {
    id: 'crystal-growth',
    name: 'Crystal Growth',
    slug: 'crystal-growth',
    description: 'Procedural crystal formation simulation',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Procedural', 'Growth'],
    tags: ['crystal', 'growth', 'procedural'],
    icon: '💎',
    features: ['Growth algorithm', 'Material refraction', 'Dynamic generation']
  },
  {
    id: 'plasma-field',
    name: 'Plasma Field',
    slug: 'plasma-field',
    description: 'Dynamic plasma field visualization using fractal noise',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Shaders', 'Noise'],
    tags: ['plasma', 'field', 'fractal'],
    icon: '🔥',
    features: ['Fractal noise', 'Color gradients', 'Real-time animation']
  },
  {
    id: 'cosmic-dust',
    name: 'Cosmic Dust',
    slug: 'cosmic-dust',
    description: 'Swirling cosmic dust particles with turbulence',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Particles', 'Motion'],
    tags: ['space', 'dust', 'turbulence'],
    icon: '✨',
    features: ['Spiral motion', 'Turbulence', 'Depth-based coloring']
  },
  {
    id: 'holographic-cube',
    name: 'Holographic Cube',
    slug: 'holographic-cube',
    description: 'Iridescent holographic material on animated geometry',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Materials', 'Animation'],
    tags: ['holographic', 'iridescent', 'geometry'],
    icon: '🎭',
    features: ['Holographic material', 'Wave animation', 'Neon colors']
  },
  {
    id: 'magnetic-field',
    name: 'Magnetic Field',
    slug: 'magnetic-field',
    description: 'Magnetic field lines visualization with dipoles',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Physics', 'Fields'],
    tags: ['magnetic', 'field', 'physics'],
    icon: '🧲',
    features: ['Dipole simulation', 'Field lines', 'Particle behavior']
  },
  {
    id: 'fireworks',
    name: 'Fireworks',
    slug: 'fireworks',
    description: 'Realistic fireworks display with particle trails',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Particles', 'Effects'],
    tags: ['fireworks', 'explosion', 'celebration'],
    icon: '🎆',
    features: ['Particle explosion', 'Gravity physics', 'Trail effects']
  },
  {
    id: 'terrain-generator',
    name: 'Terrain Generator',
    slug: 'terrain-generator',
    description: 'Procedural terrain generation using fractal noise',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Noise', 'Procedural'],
    tags: ['terrain', 'landscape', 'procedural'],
    icon: '🏔️',
    features: ['Fractal terrain', 'Height mapping', 'Wireframe view']
  },
  {
    id: 'double-pendulum',
    name: 'Double Pendulum',
    slug: 'double-pendulum',
    description: 'Chaotic motion of a double pendulum system',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Physics', 'Chaos'],
    tags: ['pendulum', 'chaos', 'physics'],
    icon: '🎢',
    features: ['Chaos theory', 'Motion trails', 'Real physics']
  },
  {
    id: 'swarm-intelligence',
    name: 'Swarm Intelligence',
    slug: 'swarm-intelligence',
    description: 'Boids algorithm simulating swarm behavior',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'AI', 'Boids'],
    tags: ['swarm', 'boids', 'flocking'],
    icon: '🐦',
    features: ['Flocking behavior', 'Collision avoidance', 'Group dynamics']
  },
  {
    id: 'aurora',
    name: 'Aurora',
    slug: 'aurora',
    description: 'Northern lights simulation with flowing particles',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Particles', 'Flow'],
    tags: ['aurora', 'northern-lights', 'particles'],
    icon: '🌌',
    features: ['Wave motion', 'Color gradients', 'Flow fields']
  },
  {
    id: 'kaleidoscope',
    name: 'Kaleidoscope',
    slug: 'kaleidoscope',
    description: 'Interactive kaleidoscope patterns with shaders',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Shaders', 'Patterns'],
    tags: ['kaleidoscope', 'patterns', 'symmetry'],
    icon: '🔮',
    features: ['Symmetry transformation', 'Animated patterns', 'Shader effects']
  },
  {
    id: 'flocking-behavior',
    name: 'Flocking Behavior',
    slug: 'flocking-behavior',
    description: 'Bird flock simulation with attraction and repulsion',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Simulation', 'Forces'],
    tags: ['flocking', 'birds', 'behavior'],
    icon: '🕊️',
    features: ['Attraction forces', 'Repulsion zones', 'Group movement']
  },
  {
    id: 'snowfall',
    name: 'Snowfall',
    slug: 'snowfall',
    description: 'Realistic snowfall with wind and accumulation',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Particles', 'Weather'],
    tags: ['snow', 'weather', 'particles'],
    icon: '❄️',
    features: ['Wind effects', 'Soft particles', 'Depth sorting']
  },
  {
    id: 'cloud-formation',
    name: 'Cloud Formation',
    slug: 'cloud-formation',
    description: 'Volumetric cloud generation with fractal noise',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Noise', 'Volume'],
    tags: ['clouds', 'volumetric', 'weather'],
    icon: '☁️',
    features: ['Volumetric rendering', 'Fractal details', 'Atmospheric']
  },
  {
    id: 'ocean-waves',
    name: 'Ocean Waves',
    slug: 'ocean-waves',
    description: 'Realistic ocean surface with Gerstner waves',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Shaders', 'Water'],
    tags: ['ocean', 'waves', 'water'],
    icon: '🌊',
    features: ['Gerstner waves', 'Foam rendering', 'Dynamic surface']
  },
  {
    id: 'galaxy-formation',
    name: 'Galaxy Formation',
    slug: 'galaxy-formation',
    description: 'Time-lapse galaxy formation from gas cloud',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Particles', 'Astronomy'],
    tags: ['galaxy', 'formation', 'space'],
    icon: '🌌',
    features: ['Spiral formation', 'Time evolution', 'Star clustering']
  },
  {
    id: 'tidal-force',
    name: 'Tidal Force',
    slug: 'tidal-force',
    description: 'Tidal forces between massive bodies',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Physics', 'Gravity'],
    tags: ['tidal', 'gravity', 'physics'],
    icon: '🌕',
    features: ['Dual bodies', 'Tidal effects', 'Force visualization']
  }
);

// Phase 2 FX Artist Level Demos
WEBGL_DEMOS.push(
  {
    id: 'fluid-vortex',
    name: 'Fluid Vortex',
    slug: 'fluid-vortex',
    description: 'Swirling fluid simulation with curl noise',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'GLSL', 'Fluid Dynamics'],
    tags: ['fluid', 'vortex', 'simulation'],
    icon: '🌀',
    features: ['Curl noise', '50K particles', 'Custom shaders']
  },
  {
    id: 'ink-drop',
    name: 'Ink Drop',
    slug: 'ink-drop',
    description: 'Realistic ink diffusion in water',
    category: DEMO_CATEGORIES.EFFECT,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Physics', 'Instancing'],
    tags: ['ink', 'diffusion', 'fluid'],
    icon: '🖤',
    features: ['Physics-based', 'Diffusion algorithm', 'Color mixing']
  },
  {
    id: 'smoke-plumes',
    name: 'Smoke Plumes',
    slug: 'smoke-plumes',
    description: 'Volumetric smoke simulation with wind',
    category: DEMO_CATEGORIES.EFFECT,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Volumetric', 'Particles'],
    tags: ['smoke', 'volumetric', 'wind'],
    icon: '💨',
    features: ['Volumetric rendering', 'Wind physics', 'Turbulence']
  },
  {
    id: 'starburst',
    name: 'Starburst',
    slug: 'starburst',
    description: 'Explosive radial particle effect',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'GLSL', 'Particles'],
    tags: ['explosion', 'radial', 'starburst'],
    icon: '💥',
    features: ['Radial explosion', 'Multi-layer', 'Custom shaders']
  },
  {
    id: 'quantum-foam',
    name: 'Quantum Foam',
    slug: 'quantum-foam',
    description: 'Quantum fluctuations at Planck scale',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'GLSL', 'Quantum'],
    tags: ['quantum', 'physics', 'particles'],
    icon: '⚛️',
    features: ['Quantum noise', '100K particles', 'Energy visualization']
  },
  {
    id: 'nebula-birth',
    name: 'Nebula Birth',
    slug: 'nebula-birth',
    description: 'Star-forming nebula simulation',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Particles', 'Astronomy'],
    tags: ['nebula', 'space', 'birth'],
    icon: '🌠',
    features: ['Gaussian distribution', '50K particles', 'Birth animation']
  },
  {
    id: 'electromagnetic',
    name: 'Electromagnetic Field',
    slug: 'electromagnetic',
    description: 'Charged particles in electromagnetic field',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Physics', 'Fields'],
    tags: ['electromagnetic', 'field', 'charges'],
    icon: '⚡',
    features: ['Lorentz force', 'Field lines', 'Charged particles']
  },
  {
    id: 'vector-field',
    name: 'Vector Field',
    slug: 'vector-field',
    description: '3D vector field visualization',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Math', 'Visualization'],
    tags: ['vector', 'field', 'flow'],
    icon: '🎯',
    features: ['3D field', 'Flow tracking', 'Arrow visualization']
  },
  {
    id: 'water-caustics',
    name: 'Water Caustics',
    slug: 'water-caustics',
    description: 'Light refraction through water surface',
    category: DEMO_CATEGORIES.EFFECT,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'GLSL', 'Optics'],
    tags: ['water', 'caustics', 'light'],
    icon: '💧',
    features: ['Light refraction', 'Wave animation', 'Caustics pattern']
  },
  {
    id: 'interactive-particles',
    name: 'Interactive Particles',
    slug: 'interactive-particles',
    description: 'Mouse-responsive particle system',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Interaction', 'Particles'],
    tags: ['interactive', 'mouse', 'particles'],
    icon: '🖱️',
    features: ['Mouse tracking', 'Real-time response', 'Force fields']
  },
  
  // Phase 3: Interactive Demos (continued)
  {
    id: 'audio-reactive-field',
    name: 'Audio Reactive Field',
    slug: 'audio-reactive-field',
    description: 'Sound-responsive visual field',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Web Audio API', 'FFT'],
    tags: ['audio', 'reactive', 'visualization'],
    icon: '🎵',
    features: ['Audio analysis', 'Real-time visualization', 'Frequency mapping']
  },
  {
    id: 'touch-ripples',
    name: 'Touch Ripples',
    slug: 'touch-ripples',
    description: 'Interactive ripple effects',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Touch Events', 'Wave Physics'],
    tags: ['touch', 'ripples', 'interaction'],
    icon: '💧',
    features: ['Touch interaction', 'Wave propagation', 'Interference patterns']
  },
  {
    id: 'gesture-flow',
    name: 'Gesture Flow',
    slug: 'gesture-flow',
    description: 'Gesture-controlled particle flow',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Gesture Recognition', 'Flow Fields'],
    tags: ['gesture', 'flow', 'particles'],
    icon: '✋',
    features: ['Gesture tracking', 'Flow control', 'Particle dynamics']
  },
  {
    id: 'drawing-particles',
    name: 'Drawing Particles',
    slug: 'drawing-particles',
    description: 'Draw with particle trails',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Drawing', 'Particles'],
    tags: ['drawing', 'particles', 'creative'],
    icon: '✏️',
    features: ['Drawing interface', 'Particle trails', 'Creative tools']
  },
  {
    id: 'magnetic-cursor',
    name: 'Magnetic Cursor',
    slug: 'magnetic-cursor',
    description: 'Cursor with magnetic field effect',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Magnetic Fields', 'Cursor Tracking'],
    tags: ['cursor', 'magnetic', 'physics'],
    icon: '🧲',
    features: ['Magnetic forces', 'Cursor tracking', 'Field visualization']
  },
  {
    id: 'gravity-well-interactive',
    name: 'Gravity Well Interactive',
    slug: 'gravity-well-interactive',
    description: 'Interactive gravity simulation',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Gravity Physics', 'N-body'],
    tags: ['gravity', 'interactive', 'physics'],
    icon: '🌌',
    features: ['Gravity wells', 'Interactive placement', 'Orbit dynamics']
  },
  {
    id: 'color-mixer',
    name: 'Color Mixer',
    slug: 'color-mixer',
    description: 'Interactive color mixing system',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Color Theory', 'Mixing'],
    tags: ['color', 'mixing', 'interactive'],
    icon: '🎨',
    features: ['Color mixing', 'Interactive drops', 'Blending modes']
  },
  {
    id: 'wave-interference',
    name: 'Wave Interference',
    slug: 'wave-interference',
    description: 'Interactive wave interference patterns',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Wave Physics', 'Interference'],
    tags: ['waves', 'interference', 'physics'],
    icon: '🌊',
    features: ['Wave sources', 'Interference patterns', 'Interactive control']
  },
  {
    id: 'particle-emitter',
    name: 'Particle Emitter',
    slug: 'particle-emitter',
    description: 'Customizable particle emitter system',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Particle Systems', 'Emitters'],
    tags: ['particles', 'emitter', 'customizable'],
    icon: '💫',
    features: ['Emitter patterns', 'Customizable parameters', 'Multiple emitters']
  },
  
  // Phase 4: Experimental Demos
  {
    id: 'reaction-diffusion',
    name: 'Reaction-Diffusion',
    slug: 'reaction-diffusion',
    description: 'Gray-Scott reaction-diffusion system',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'GPGPU', 'Chemical Systems'],
    tags: ['reaction', 'diffusion', 'chemistry'],
    icon: '🧪',
    features: ['Chemical reactions', 'Pattern formation', 'GPU computation']
  },
  {
    id: 'volumetric-light',
    name: 'Volumetric Light',
    slug: 'volumetric-light',
    description: 'Ray-marched volumetric lighting',
    category: DEMO_CATEGORIES.VISUAL_EFFECTS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Ray Marching', 'Volumetrics'],
    tags: ['volumetric', 'light', 'raymarching'],
    icon: '💡',
    features: ['Volumetric rendering', 'God rays', 'Atmospheric scattering']
  },
  {
    id: 'sdf-morphing',
    name: 'SDF Morphing',
    slug: 'sdf-morphing',
    description: 'Signed Distance Field shape morphing',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'SDF', 'Shape Morphing'],
    tags: ['sdf', 'morphing', 'advanced'],
    icon: '🔮',
    features: ['SDF operations', 'Smooth morphing', 'Complex shapes']
  },
  {
    id: 'ray-marching',
    name: 'Ray Marching',
    slug: 'ray-marching',
    description: 'Fractal world using ray marching',
    category: DEMO_CATEGORIES.VISUAL_EFFECTS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Ray Marching', 'Fractals'],
    tags: ['raymarching', 'fractal', 'advanced'],
    icon: '🌀',
    features: ['Fractal geometry', 'Infinite detail', 'Real-time rendering']
  },
  {
    id: 'compute-shader',
    name: 'Compute Shader',
    slug: 'compute-shader',
    description: 'GPU compute particles simulation',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Compute Shaders', 'GPGPU'],
    tags: ['compute', 'gpu', 'particles'],
    icon: '🖥️',
    features: ['GPU computation', '65k particles', 'Strange attractors']
  },
  
  // Template-based Demos
  {
    id: 'lava-flow',
    name: 'Lava Flow',
    slug: 'lava-flow',
    description: 'Molten lava simulation',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Noise Field', 'Thermal Dynamics'],
    tags: ['lava', 'flow', 'thermal'],
    icon: '🌋',
    features: ['Lava flow', 'Heat visualization', 'Fractal noise']
  },
  {
    id: 'rope-physics',
    name: 'Rope Physics',
    slug: 'rope-physics',
    description: 'Physical rope simulation',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Spring Physics', 'Constraints'],
    tags: ['rope', 'physics', 'springs'],
    icon: '🪢',
    features: ['Spring system', 'Rope dynamics', 'Physical constraints']
  },
  {
    id: 'cloth-simulation',
    name: 'Cloth Simulation',
    slug: 'cloth-simulation',
    description: 'Fabric physics simulation',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Cloth Physics', 'Mass-Spring'],
    tags: ['cloth', 'fabric', 'physics'],
    icon: '🏳️',
    features: ['Cloth physics', 'Wind effects', 'Material properties']
  },
  {
    id: 'plasma-storm',
    name: 'Plasma Storm',
    slug: 'plasma-storm',
    description: 'Turbulent plasma field',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Turbulence', 'Plasma Physics'],
    tags: ['plasma', 'storm', 'turbulence'],
    icon: '⚡',
    features: ['Plasma dynamics', 'Turbulent flow', 'Energy fields']
  },
  {
    id: 'crystalline-rain',
    name: 'Crystalline Rain',
    slug: 'crystalline-rain',
    description: 'Crystal formation rain effect',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Crystal Geometry', 'Particle Rain'],
    tags: ['crystal', 'rain', 'geometry'],
    icon: '💎',
    features: ['Crystal shapes', 'Rain physics', 'Refraction effects']
  },
  {
    id: 'gravitational-lens',
    name: 'Gravitational Lens',
    slug: 'gravitational-lens',
    description: 'Light bending simulation',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Relativity', 'Gravitational Lensing'],
    tags: ['gravity', 'lens', 'relativity'],
    icon: '🔍',
    features: ['Gravitational lensing', 'Light bending', 'Space-time curvature']
  },
  {
    id: 'potential-field',
    name: 'Potential Field',
    slug: 'potential-field',
    description: 'Scalar potential field visualization',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Scalar Fields', 'Gradient Visualization'],
    tags: ['potential', 'field', 'scalar'],
    icon: '📊',
    features: ['Potential fields', 'Gradient mapping', 'Field lines']
  },
  {
    id: 'force-field',
    name: 'Force Field',
    slug: 'force-field',
    description: 'Vector force field visualization',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Vector Fields', 'Force Visualization'],
    tags: ['force', 'field', 'vector'],
    icon: '🧲',
    features: ['Force vectors', 'Field visualization', 'Particle dynamics']
  },
  {
    id: 'viscous-flow',
    name: 'Viscous Flow',
    slug: 'viscous-flow',
    description: 'Viscous fluid dynamics',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Fluid Dynamics', 'Viscosity'],
    tags: ['viscous', 'flow', 'fluid'],
    icon: '🍯',
    features: ['Viscous flow', 'Fluid dynamics', 'Drag forces']
  },
  {
    id: 'fractal-tree',
    name: 'Fractal Tree',
    slug: 'fractal-tree',
    description: 'Recursive fractal tree generation',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Fractals', 'L-systems'],
    tags: ['fractal', 'tree', 'recursive'],
    icon: '🌳',
    features: ['Fractal generation', 'Recursive branching', 'Natural forms']
  },
  {
    id: 'parametric-surface',
    name: 'Parametric Surface',
    slug: 'parametric-surface',
    description: 'Mathematical surface visualization',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Parametric Equations', 'Surface Geometry'],
    tags: ['parametric', 'surface', 'math'],
    icon: '📐',
    features: ['Parametric surfaces', 'Mathematical forms', 'Dynamic morphing']
  },
  {
    id: 'torus-field',
    name: 'Torus Field',
    slug: 'torus-field',
    description: 'Toroidal particle field',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Toroidal Geometry', 'Particle Fields'],
    tags: ['torus', 'field', 'particles'],
    icon: '🍩',
    features: ['Toroidal flow', 'Particle orbits', 'Field dynamics']
  },
  {
    id: 'ring-wave',
    name: 'Ring Wave',
    slug: 'ring-wave',
    description: 'Circular wave propagation',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', 'Wave Propagation', 'Circular Patterns'],
    tags: ['ring', 'wave', 'propagation'],
    icon: '⭕',
    features: ['Ring waves', 'Wave propagation', 'Interference']
  },
  {
    id: 'voronoi-pattern',
    name: 'Voronoi Pattern',
    slug: 'voronoi-pattern',
    description: 'Voronoi diagram generation',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'Voronoi', 'Computational Geometry'],
    tags: ['voronoi', 'pattern', 'cells'],
    icon: '🔶',
    features: ['Voronoi cells', 'Pattern generation', 'Cell dynamics']
  },
  {
    id: 'magnetic-pendulum',
    name: 'Magnetic Pendulum',
    slug: 'magnetic-pendulum',
    description: 'Chaotic magnetic pendulum',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Chaos Theory', 'Magnetic Forces'],
    tags: ['magnetic', 'pendulum', 'chaos'],
    icon: '🧲',
    features: ['Chaotic motion', 'Magnetic fields', 'Strange attractors']
  },
  {
    id: 'string-theory',
    name: 'String Theory',
    slug: 'string-theory',
    description: 'Vibrating string visualization',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 3,
    technologies: ['Three.js', 'String Physics', 'Wave Equations'],
    tags: ['string', 'vibration', 'theory'],
    icon: '🎻',
    features: ['String vibrations', 'Wave modes', 'Harmonic analysis']
  },
  {
    id: 'tensor-field',
    name: 'Tensor Field',
    slug: 'tensor-field',
    description: 'Tensor field visualization',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Tensor Mathematics', 'Field Theory'],
    tags: ['tensor', 'field', 'math'],
    icon: '🔢',
    features: ['Tensor fields', 'Multi-dimensional data', 'Field visualization']
  },
  {
    id: 'hyperbolic-geometry',
    name: 'Hyperbolic Geometry',
    slug: 'hyperbolic-geometry',
    description: 'Non-Euclidean geometry visualization',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Non-Euclidean Geometry', 'Hyperbolic Space'],
    tags: ['hyperbolic', 'geometry', 'non-euclidean'],
    icon: '🌐',
    features: ['Hyperbolic space', 'Non-Euclidean forms', 'Poincaré disk']
  },
  {
    id: 'planar-field',
    name: 'Planar Field',
    slug: 'planar-field',
    description: '2D field visualization in 3D space',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 2,
    technologies: ['Three.js', '2D Fields', 'Height Maps'],
    tags: ['planar', 'field', '2d'],
    icon: '📋',
    features: ['2D fields', 'Height mapping', 'Field animation']
  },
  {
    id: 'spherical-harmonics',
    name: 'Spherical Harmonics',
    slug: 'spherical-harmonics',
    description: 'Spherical harmonic functions',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Spherical Harmonics', 'Mathematical Functions'],
    tags: ['spherical', 'harmonics', 'math'],
    icon: '🌐',
    features: ['Harmonic functions', 'Spherical shapes', 'Mode visualization']
  },
  
  // Category Deep Dive Demos
  {
    id: 'quantum-entanglement',
    name: 'Quantum Entanglement',
    slug: 'quantum-entanglement',
    description: 'Entangled particle visualization',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Quantum Physics', 'Entanglement'],
    tags: ['quantum', 'entanglement', 'physics'],
    icon: '🔗',
    features: ['Quantum states', 'Entangled pairs', 'Correlation visualization']
  },
  {
    id: 'black-hole-accretion',
    name: 'Black Hole Accretion',
    slug: 'black-hole-accretion',
    description: 'Accretion disk simulation',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Astrophysics', 'Accretion Dynamics'],
    tags: ['blackhole', 'accretion', 'astrophysics'],
    icon: '⚫',
    features: ['Accretion disk', 'Relativistic effects', 'Event horizon']
  },
  {
    id: 'bioluminescence',
    name: 'Bioluminescence',
    slug: 'bioluminescence',
    description: 'Glowing organisms simulation',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Bioluminescence', 'Organic Animation'],
    tags: ['bio', 'luminescence', 'glow'],
    icon: '🦋',
    features: ['Bioluminescent effects', 'Organic motion', 'Glow simulation']
  },
  {
    id: 'electric-field-visualization',
    name: 'Electric Field',
    slug: 'electric-field-visualization',
    description: 'Electric field line visualization',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Electromagnetism', 'Field Lines'],
    tags: ['electric', 'field', 'physics'],
    icon: '⚡',
    features: ['Electric fields', 'Field lines', 'Charge interactions']
  },
  {
    id: 'crystal-lattice',
    name: 'Crystal Lattice',
    slug: 'crystal-lattice',
    description: 'Atomic crystal structure',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Crystallography', 'Atomic Structure'],
    tags: ['crystal', 'lattice', 'atomic'],
    icon: '💎',
    features: ['Crystal structures', 'Atomic lattice', 'Phonon vibrations']
  },
  {
    id: 'wave-packet',
    name: 'Wave Packet',
    slug: 'wave-packet',
    description: 'Quantum wave packet dynamics',
    category: DEMO_CATEGORIES.NOISE,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Quantum Mechanics', 'Wave Packets'],
    tags: ['wave', 'packet', 'quantum'],
    icon: '🌊',
    features: ['Wave packets', 'Dispersion', 'Group velocity']
  },
  {
    id: 'superfluid',
    name: 'Superfluid',
    slug: 'superfluid',
    description: 'Quantum fluid dynamics',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Superfluidity', 'Quantum Vortices'],
    tags: ['superfluid', 'quantum', 'vortex'],
    icon: '💧',
    features: ['Superfluid flow', 'Quantum vortices', 'Zero viscosity']
  },
  {
    id: 'magnetohydrodynamics',
    name: 'Magnetohydrodynamics',
    slug: 'magnetohydrodynamics',
    description: 'Plasma physics simulation',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Plasma Physics', 'MHD'],
    tags: ['mhd', 'plasma', 'magnetic'],
    icon: '🌌',
    features: ['Plasma dynamics', 'Magnetic confinement', 'MHD waves']
  },
  {
    id: 'cellular-automata-3d',
    name: '3D Cellular Automata',
    slug: 'cellular-automata-3d',
    description: 'Three-dimensional Game of Life',
    category: DEMO_CATEGORIES.GENERATIVE,
    component: null,
    implemented: true,
    difficulty: 4,
    technologies: ['Three.js', 'Cellular Automata', '3D Life'],
    tags: ['cellular', 'automata', '3d'],
    icon: '🧊',
    features: ['3D cellular rules', 'Emergent patterns', 'Life simulation']
  },
  
  // Hybrid Expression Demos
  {
    id: 'audio-visual-synaesthesia',
    name: 'Audio-Visual Synaesthesia',
    slug: 'audio-visual-synaesthesia',
    description: 'Sound-to-visual synthesis',
    category: DEMO_CATEGORIES.PARTICLES,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Audio Analysis', 'Visual Synthesis'],
    tags: ['audio', 'visual', 'synaesthesia'],
    icon: '🎶',
    features: ['Audio-visual sync', 'Frequency analysis', 'Synaesthetic mapping']
  },
  {
    id: 'fluid-particle-interaction',
    name: 'Fluid-Particle Interaction',
    slug: 'fluid-particle-interaction',
    description: 'Hybrid fluid dynamics system',
    category: DEMO_CATEGORIES.SIMULATION,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Fluid Dynamics', 'Particle Systems'],
    tags: ['fluid', 'particles', 'hybrid'],
    icon: '💫',
    features: ['Fluid-particle coupling', 'Hybrid simulation', 'Complex dynamics']
  },
  {
    id: 'geometric-morphing-field',
    name: 'Geometric Morphing Field',
    slug: 'geometric-morphing-field',
    description: 'Shape morphing with noise fields',
    category: DEMO_CATEGORIES.GEOMETRY,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Shape Morphing', 'Noise Fields'],
    tags: ['morphing', 'field', 'hybrid'],
    icon: '🔮',
    features: ['Geometric morphing', 'Noise displacement', 'Hybrid effects']
  },
  {
    id: 'generative-art-system',
    name: 'Generative Art System',
    slug: 'generative-art-system',
    description: 'Multi-technique art generator',
    category: DEMO_CATEGORIES.GENERATIVE,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Generative Art', 'Multiple Techniques'],
    tags: ['generative', 'art', 'system'],
    icon: '🎨',
    features: ['Rule-based generation', 'Multi-technique', 'Interactive art']
  },
  {
    id: 'quantum-field-visualization',
    name: 'Quantum Field Visualization',
    slug: 'quantum-field-visualization',
    description: 'Quantum field theory visualization',
    category: DEMO_CATEGORIES.PHYSICS,
    component: null,
    implemented: true,
    difficulty: 5,
    technologies: ['Three.js', 'Quantum Field Theory', 'Advanced Physics'],
    tags: ['quantum', 'field', 'visualization'],
    icon: '⚛️',
    features: ['Quantum fields', 'Virtual particles', 'Field interactions']
  }
);